import { useEffect, useState, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Row, Col, Form, Input, Select, Table, Button, Divider, Space, Tag, Typography, message, Tooltip, Card, theme, Flex } from 'antd';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { LaborFeePaymentCheckList, conferenceTypes } from '../constants';
import { financeActions } from '../store/financeSlice';
import { TABLE_PAGE_SIZE_OPTIONS, filterFormItemLayout, filterFormGridLayout, filterFormItemStyle, computeButtonColLayout } from '@utils/property';
import { fetchLaborFeeProjects } from '../../../services/Finance/FinanceLaborFee.service';
import { numberProject } from '../../../utils/utils';
import { localStorageGet } from '@core/cookie';
import copy from 'copy-to-clipboard';
import LaborFeeDetailDrawer from './components/LaborFeeDetailDrawer';
import LaborFeeApprovalDrawer from './components/LaborFeeApprovalDrawer';
import LaborFeePaymentDrawer from './components/LaborFeePaymentDrawer';
import './FinanceLaborFeeProjects.less';
import PageHeader from '@components/PageHeader/PageHeader';

const { setLaborFeeProjectFilter: setFilter, setLaborFeeProjectCursor: setCursor } = financeActions;
const { Text } = Typography;

export default function FinanceLaborFeeProjects() {
    const location = useLocation();

    const dispatch = useDispatch();
    const filter = useSelector(state => state.finance.laborFeeProject.filter);
    const cursor = useSelector(state => state.finance.laborFeeProject.cursor);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const [form] = Form.useForm();

    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    // 劳务费明细弹窗
    const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);

    // 劳务费审批弹窗
    const [approvalDrawerOpen, setApprovalDrawerOpen] = useState(false);
    const [selectedApprovalProject, setSelectedApprovalProject] = useState(null);

    // 劳务费付款弹窗
    const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
    const [selectedPaymentProject, setSelectedPaymentProject] = useState(null);

    // URL 参数相关
    const [selectedBatchNo, setSelectedBatchNo] = useState(null);

    // 筛选表单展开/收起
    const { token } = theme.useToken();
    const [expanded, setExpanded] = useState(false);
    const VISIBLE_FIELD_COUNT = 4;
    const TOTAL_FIELD_COUNT = 7;
    const buttonColLayout = computeButtonColLayout(expanded ? TOTAL_FIELD_COUNT : VISIBLE_FIELD_COUNT);

    // fixed-scroll 三件套：表格高度自适应 + 横向滚动守卫 + 纵向阴影
    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        document.title = '付款项目管理-财务中心';
    }, []);

    const onFinish = values => {
        if (!!values?.projectId) {
            values['projectId'] = values.projectId.replace(/^[a-z]+0+/i, '');
        }

        dispatch(
            setFilter({
                ...filter,
                ...values,
                page: 1,
            }),
        );
    };

    const onReset = () => {
        form.resetFields();

        dispatch(
            setFilter({
                ...filter,
                ...form.getFieldsValue(),
                page: 1,
            }),
        );
    };

    const onChangePage = (page, pageSize) => {
        dispatch(
            setFilter({
                ...filter,
                page,
                pageSize,
            }),
        );
    };

    const onShowSizeChange = (page, pageSize) => onChangePage(1, pageSize);

    // 打开劳务费明细弹窗
    const openLaborFeeDetail = project => {
        setSelectedProject(project);
        setDetailDrawerOpen(true);
    };

    // 关闭劳务费明细弹窗
    const closeLaborFeeDetail = () => {
        setDetailDrawerOpen(false);
        setSelectedProject(null);
    };

    // 打开劳务费审批弹窗
    const openLaborFeeApproval = project => {
        setSelectedApprovalProject(project);
        setApprovalDrawerOpen(true);
    };

    // 关闭劳务费审批弹窗
    const closeLaborFeeApproval = () => {
        setApprovalDrawerOpen(false);
        setSelectedApprovalProject(null);
        setSelectedBatchNo(null);
    };

    // 打开劳务费付款弹窗
    const openLaborFeePayment = project => {
        setSelectedPaymentProject(project);
        setPaymentDrawerOpen(true);
    };

    // 关闭劳务费付款弹窗
    const closeLaborFeePayment = () => {
        setPaymentDrawerOpen(false);
        setSelectedPaymentProject(null);
        setSelectedBatchNo(null);
    };

    // 是否拥有财务劳务费付款人员权限
    const isLaborFeePayment = useMemo(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        // console.log(roleList);
        if (roleList && roleList.length) {
            // console.log('财务劳务费付款人员: ', roleList.some(item => item.name === '财务劳务费付款人员'));
            return roleList.some(item => item.name === '财务劳务费付款人员');
        }
        return false;
    }, []);

    // 是否拥有财务劳务费管理员权限
    const isLaborFeeManager = useMemo(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        if (roleList && roleList.length) {
            return roleList.some(item => item.name === '财务劳务费管理员');
        }
        return false;
    }, []);

    const copyClick = msg => {
        copy(msg);
        message.success('已复制到剪贴板');
    };

    const columns = [
        {
            title: '项目名称',
            dataIndex: 'projectName',
            width: 300,
            render: (_, record) => (
                <>
                    <div className="projectName">
                        <span>{record.projectName}</span>
                        {!!record.type && (
                            <Tag color="volcano" style={{ marginLeft: 4, marginRight: 0, padding: 2, fontSize: 10, lineHeight: '1em', verticalAlign: 'middle' }}>
                                {conferenceTypes.find(item => item.type === record.type).label}
                            </Tag>
                        )}
                        {!!record.isTesting && <Tag color="#f50">TEST</Tag>}
                    </div>
                    <div className="h3yunNo">
                        氚云项目编号：{!!record.h3yunNo ? record.h3yunNo : <Text type="secondary">&ndash;</Text>}
                        {!!record.h3yunNo && (
                            <span onClick={() => copyClick(record.h3yunNo)}>
                                <i className="bi bi-copy"></i>
                            </span>
                        )}
                    </div>
                    <div className="projectNo">
                        会议项目编号：{numberProject('YHY', record.projectId)}
                        <span onClick={() => copyClick(numberProject('YHY', record.projectId))}>
                            <i className="bi bi-copy"></i>
                        </span>
                    </div>
                </>
            ),
        },
        {
            title: '执行日期',
            dataIndex: 'projectDates',
            width: 140,
            render: (_, record) => (!!record.dates ? record.dates.split(',').map(d => <Tag key={d}>{d}</Tag>) : <Text type="secondary">&ndash;</Text>),
        },
        {
            title: '财务经理审核',
            dataIndex: 'node5',
            width: 80,
            render: (_, record) => (!!record.node5 ? record.node5 : 0),
        },
        {
            title: '财务总监审批',
            dataIndex: 'node6',
            width: 80,
            render: (_, record) => (!!record.node6 ? record.node6 : 0),
        },
        {
            title: '待付款申请',
            dataIndex: 'node10',
            width: 80,
            render: (_, record) => (!!record.node10 ? record.node10 : 0),
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                    {isLaborFeeManager && (
                        <>
                            <Button size="small" type="link" onClick={() => openLaborFeeDetail(record)}>
                                劳务费明细
                            </Button>
                            <Button size="small" type="link" onClick={() => openLaborFeeApproval(record)}>
                                劳务费审批
                            </Button>
                        </>
                    )}
                    {(isLaborFeePayment || isLaborFeeManager) && (
                        <Button size="small" type="link" onClick={() => openLaborFeePayment(record)}>
                            劳务费付款
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    // 获取付款项目列表
    const getLaborFeeProjects = async () => {
        try {
            setLoading(true);

            const params = {
                ...filter,
                // projectCode: 'YHY', // 目前只有医会议
                // type: 1,
            };
            const res = await fetchLaborFeeProjects(params);
            if (res.status_code === 200) {
                setTotal(res.data.totalCount);
                setTableData(res.data.list);

                // 检查是否存在URL参数
                // 使用原生window.location.search获取最新URL参数状态
                const currentSearchParams = new URLSearchParams(window.location.search);
                const currentProjectId = currentSearchParams.get('projectId');
                if (currentProjectId) {
                    handleURLParams(res.data.list);
                }
            }

            setLoading(false);
        } catch (error) {
            console.log('获取付款项目列表失败:', error);
            setLoading(false);
        }
    };

    // 处理 URL 参数
    const handleURLParams = list => {
        const searchParams = new URLSearchParams(window.location.search);
        console.log();
        const openApprovalBatch = searchParams.get('openApprovalBatch') === 'true';
        const openPaymentBatch = searchParams.get('openPaymentBatch') === 'true';
        const projectId = searchParams.get('projectId');
        const batchNo = searchParams.get('batchNo');
        if ((openApprovalBatch || openPaymentBatch) && projectId) {
            // 查找对应的项目
            const targetProject = list.find(p => p.projectId === parseInt(projectId, 10));
            if (targetProject) {
                // 设置 batchNo，传递给弹框组件
                if (batchNo) {
                    setSelectedBatchNo(batchNo);
                }
                if (openApprovalBatch) {
                    openLaborFeeApproval(targetProject);
                } else if (openPaymentBatch) {
                    openLaborFeePayment(targetProject);
                }
            }
        }
    };

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const handleRefresh = useCallback(() => {
        getLaborFeeProjects();
    }, [getLaborFeeProjects]);

    useEffect(() => {
        form.setFieldsValue(filter);

        getLaborFeeProjects();
    }, [filter, approvalDrawerOpen, paymentDrawerOpen]);

    // 根据 URL 参数检索projectId
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const projectId = searchParams.get('projectId');

        if (projectId) {
            dispatch(
                setFilter({
                    ...filter,
                    projectId,
                    page: 1,
                    isTesting: undefined,
                }),
            );
        }
    }, [location.search]);

    return (
        <div className="page-container list-page-container finance-laborfee-list-page">
            <PageHeader title="付款项目管理" breadcrumb={[{ label: '财务中心' }, { label: '付款项目管理' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <Card
                    size="small"
                    className="finance-laborfee-filter-panel"
                    style={{
                        marginBottom: token.marginLG,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillAlter,
                    }}
                >
                    <Form {...filterFormItemLayout} onFinish={onFinish} form={form} autoComplete="off" requiredMark={false}>
                        <Row gutter={[16, 16]}>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="项目名称" name="projectName" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="项目名称关键字" />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="氚云项目编号" name="h3yunNo" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="氚云项目编号" />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="项目编号" name="projectId" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="项目编号" />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="财务经理待审" name="node5" style={filterFormItemStyle}>
                                    <Select placeholder="审核状态" allowClear options={LaborFeePaymentCheckList} fieldNames={{ value: 'status', label: 'label' }} />
                                </Form.Item>
                            </Col>

                            {expanded && (
                                <>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item label="财务总监待审" name="node6" style={filterFormItemStyle}>
                                            <Select placeholder="审批状态" allowClear options={LaborFeePaymentCheckList} fieldNames={{ value: 'status', label: 'label' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item label="出纳待付款" name="node10" style={filterFormItemStyle}>
                                            <Select placeholder="是否有待付款" allowClear>
                                                <Select.Option value={1}>有待付款</Select.Option>
                                                <Select.Option value={2}>无待付款</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item label="正式/测试" name="isTesting" initialValue={0} style={filterFormItemStyle}>
                                            <Select placeholder="是否测试项目" allowClear>
                                                <Select.Option value={0}>正式项目</Select.Option>
                                                <Select.Option value={1}>测试项目</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </>
                            )}

                            <Col {...buttonColLayout}>
                                <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                                    <Space size="small">
                                        <Button type="primary" htmlType="submit" icon={<i className="bi bi-search" aria-hidden="true" />}>
                                            检索
                                        </Button>
                                        <Button type="default" onClick={onReset}>
                                            重置
                                        </Button>
                                        <Button type="link" size="small" aria-expanded={expanded} onClick={() => setExpanded(prev => !prev)} style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                            {expanded ? (
                                                <>
                                                    收起 <span className="bi bi-chevron-up" aria-hidden="true" />
                                                </>
                                            ) : (
                                                <>
                                                    展开 <span className="bi bi-chevron-down" aria-hidden="true" />
                                                </>
                                            )}
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container finance-laborfee-table-region">
                <div className="main-toolbar finance-laborfee-list-toolbar">
                    <div className="main-tools">
                        <Space>
                            <span className="table-title">付款项目列表</span>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={handleRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap finance-laborfee-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        rowKey="projectId"
                        columns={columns}
                        dataSource={tableData}
                        loading={loading}
                        rowClassName={row => (row.id === cursor ? 'store-cursor' : '')}
                        bordered
                        sticky
                        size="medium"
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                        pagination={{
                            total,
                            showTotal: () => `共${total}条`,
                            current: filter.page,
                            pageSize: filter.pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                            onChange: onChangePage,
                            onShowSizeChange,
                        }}
                    />
                </div>
            </div>

            {/* 劳务费明细弹窗 */}
            <LaborFeeDetailDrawer
                open={detailDrawerOpen}
                onClose={closeLaborFeeDetail}
                projectId={selectedProject?.projectId}
                // projectCode={selectedProject?.projectCode}
                projectName={selectedProject?.projectName}
            />

            {/* 劳务费审批弹窗 */}
            <LaborFeeApprovalDrawer open={approvalDrawerOpen} onClose={closeLaborFeeApproval} projectId={selectedApprovalProject?.projectId} projectCode={selectedApprovalProject?.projectCode} projectName={selectedApprovalProject?.projectName} selectedBatchNo={selectedBatchNo} />

            {/* 劳务费付款弹窗 */}
            <LaborFeePaymentDrawer open={paymentDrawerOpen} onClose={closeLaborFeePayment} projectId={selectedPaymentProject?.projectId} projectCode={selectedPaymentProject?.projectCode} projectName={selectedPaymentProject?.projectName} selectedBatchNo={selectedBatchNo} />
        </div>
    );
}
